/*
 * OrbitOptions — Stage 1 Solution Options Analysis screen.
 *
 * Spec map (v2.7 HTML lines 295-321):
 *   This page renders the three Option cards after a user picks
 *   Automated DIY at the Choice Screen AND has deep+complete clarity
 *   (auto_deep_q is YES). User picks one → Stage 2 Bypass Eligibility
 *   runs → outcome routes to one of:
 *       ① /orbit/deploy/:eid       (direct deploy — marketplace)
 *       ② /orbit/template/:eid     (template config card)
 *       ③ /orbit/handoff/:eid      (planner direct — PRD from L1.E)
 *       ④/⑤/⑥ /orbit/scope/:eid    (fall into L2)
 *
 * Cards: Marketplace (when match clears threshold) · Template (when
 * close-fit) · Custom (always). Each card shows fit / tool / cust
 * scores per spec + solves/misses + price/timeline/IP/compliance.
 *
 * Branded to AA — light surface, Plex Sans, blue primary, yellow as
 * editorial accent on the recommended card.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, ChevronLeft, Loader2, Sparkles,
  ShoppingBag, FileText, Hammer, Sun, Moon,
} from 'lucide-react';

import { useOrbitTheme } from '../useOrbitTheme';
import {
  fetchStage1Options, pickStage1Option,
  SolutionOptionCard, SolutionOptionKind, Stage1Response,
} from '../lib/stage1Api';
import { PaywallCard } from '../../components/billing/PaywallCard';
import { extractPaywall, type PaywallPayload } from '../../components/billing/paywall';

import '../styles/orbit-tokens.css';
import '../styles/orbit-intake.css';
import '../styles/orbit-dossier.css';
import './orbit-options.css';


const KIND_META: Record<SolutionOptionKind, {
  badge: string;
  icon: React.ReactNode;
  blurb: string;
}> = {
  marketplace: {
    badge: 'Marketplace',
    icon: <ShoppingBag size={16} strokeWidth={2.25} />,
    blurb: 'Pre-built. Deploy in days.',
  },
  template: {
    badge: 'Template',
    icon: <FileText size={16} strokeWidth={2.25} />,
    blurb: 'Configured to your context.',
  },
  custom: {
    badge: 'Custom',
    icon: <Hammer size={16} strokeWidth={2.25} />,
    blurb: 'Built fresh for your spec.',
  },
};


export function OrbitOptions() {
  const { theme, toggle: toggleTheme } = useOrbitTheme();
  const navigate = useNavigate();
  const { engagementId } = useParams<{ engagementId: string }>();

  const [state, setState] = useState<Stage1Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState<SolutionOptionKind | null>(null);
  // Paywall state — non-null while the user is at quota and looking at
  // the upgrade / top-up CTAs. Set when a 402 lands; cleared on retry.
  const [paywall, setPaywall] = useState<PaywallPayload | null>(null);
  // Remember the last pick so a successful top-up can resume it.
  const [pendingPick, setPendingPick] = useState<SolutionOptionCard | null>(null);

  const load = useCallback(async () => {
    if (!engagementId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStage1Options(engagementId);
      setState(data);
    } catch (e: any) {
      console.error('[stage1] options fetch failed', e);
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not load options.');
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { load(); }, [load]);

  const handlePick = useCallback(async (card: SolutionOptionCard) => {
    if (!engagementId) return;
    setPicking(card.kind);
    setPaywall(null);
    setPendingPick(card);
    try {
      const res = await pickStage1Option(
        engagementId, card.kind, card.solution_slug,
      );
      const next = res.decision.next_route;
      if (next) navigate(next);
    } catch (e) {
      const pw = extractPaywall(e);
      if (pw) {
        setPaywall(pw);
      } else {
        console.error('[stage1] pick failed', e);
      }
      setPicking(null);
    }
  }, [engagementId, navigate]);

  // After a successful top-up the user gets immediate headroom — retry
  // the pick they were trying to make so they don't have to click twice.
  const handleTopUpSuccess = useCallback(() => {
    setPaywall(null);
    if (pendingPick) {
      handlePick(pendingPick);
    }
  }, [pendingPick, handlePick]);

  // The "recommended" card is the one with the highest fit_score.
  // Marketplace beats template beats custom at equal fit (cheaper /
  // faster path). Ties broken by kind order in cards list.
  const recommendedKind = useMemo<SolutionOptionKind | null>(() => {
    if (!state || !state.cards.length) return null;
    let best: SolutionOptionCard | null = null;
    for (const c of state.cards) {
      if (!best || c.fit_score > best.fit_score) best = c;
    }
    return best ? best.kind : null;
  }, [state]);

  return (
    <div className="orbit-intake-root orbit-options-root" data-theme={theme}>
      <header className="aa-topbar">
        <div className="aa-brand">
          <button
            type="button"
            className="aa-options-back"
            onClick={() => navigate('/orbit/intake')}
            aria-label="Back to intake"
          >
            <ChevronLeft size={16} strokeWidth={2.25} />
          </button>
          <div className="aa-brand-mark" aria-hidden="true">
            <img src="/orbit/logos/aa-logo-icon-white.svg" alt="" width={18} height={18} />
          </div>
          <span className="aa-brand-name">Orbit · Solution Options</span>
        </div>
        <div className="aa-topbar-right">
          <button
            type="button"
            className="aa-theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
      </header>

      <main className="aa-options-main">
        {loading && (
          <div className="aa-options-loading">
            <Loader2 size={18} className="aa-spin" />
            <span>Scoring solutions…</span>
          </div>
        )}

        {error && (
          <div className="aa-options-error">
            <strong>Can't show options yet.</strong>
            <p>{error}</p>
            <button type="button" onClick={() => navigate('/orbit/intake')}>
              Back to intake
            </button>
          </div>
        )}

        {state && state.cards.length > 0 && (
          <>
            <section className="aa-options-hero">
              <span className="aa-options-eyebrow">
                Stage 1 · Solution Options
              </span>
              <h1 className="aa-options-title">
                <em>{state.cards.length === 1 ? 'One path' :
                     state.cards.length === 2 ? 'Two paths' :
                     'Three paths'}</em> to your build.
              </h1>
              <p className="aa-options-lead">
                {state.cards.length < 3
                  ? "No marketplace match cleared the bar for your dossier — here's what fits."
                  : "We scored what's deployable today against your dossier. Pick the one that fits — or keep scoping if you want more depth."}
              </p>
            </section>

            {paywall && (
              <section
                className="aa-options-paywall"
                style={{ marginBottom: 24 }}
              >
                <PaywallCard
                  payload={paywall}
                  onTopUpSuccess={handleTopUpSuccess}
                  onDismiss={() => setPaywall(null)}
                />
              </section>
            )}

            <section
              className={`aa-options-grid${paywall ? ' aa-options-grid--locked' : ''}`}
              style={paywall ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
              aria-disabled={!!paywall}
            >
              {state.cards.map((card) => {
                const meta = KIND_META[card.kind];
                const isRec = card.kind === recommendedKind;
                const isPicking = picking === card.kind;
                return (
                  <article
                    key={card.kind + (card.solution_slug ?? '')}
                    className={
                      `aa-option-card aa-option-card--${card.kind}` +
                      (isRec ? ' aa-option-card--rec' : '')
                    }
                  >
                    {isRec && (
                      <span className="aa-option-card-badge">
                        <Sparkles size={11} strokeWidth={2.5} />
                        <span>Recommended</span>
                      </span>
                    )}
                    <header className="aa-option-card-head">
                      <span className="aa-option-card-kind">
                        {meta.icon}
                        <span>{meta.badge}</span>
                      </span>
                      <h2 className="aa-option-card-name">{card.name}</h2>
                      <p className="aa-option-card-tagline">{card.tagline}</p>
                    </header>

                    {/* Scores — single Fit number; Stack + Custom condensed
                        into a tooltip-line so the card stops eating space. */}
                    <div className="aa-option-card-scores aa-option-card-scores--compact">
                      <div>
                        <dt>Fit</dt>
                        <dd>{Math.round(card.fit_score * 100)}%</dd>
                      </div>
                      <div className="aa-option-card-score-sub" title="Stack compatibility · Customization needed">
                        Stack {Math.round(card.tool_compat_score * 100)}% · Custom {Math.round(card.cust_needed_score * 100)}%
                      </div>
                    </div>

                    {/* Top 2 solves + top 1 miss — full lists moved behind a
                        "details" expand so the card stays scannable. */}
                    {card.solves.length > 0 && (
                      <ul className="aa-option-card-list aa-option-card-list--solves">
                        {card.solves.slice(0, 2).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    )}
                    {card.misses.length > 0 && (
                      <ul className="aa-option-card-list aa-option-card-list--misses">
                        {card.misses.slice(0, 1).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    )}

                    {/* Price + Timeline only — IP + Compliance moved into
                        the details summary below. CFO + champion both
                        want price/timeline first. */}
                    <div className="aa-option-card-meta aa-option-card-meta--compact">
                      <div><dt>Price</dt><dd>{card.price_summary}</dd></div>
                      <div><dt>Timeline</dt><dd>{card.timeline_summary}</dd></div>
                    </div>

                    {(card.ip_terms || card.compliance
                      || card.solves.length > 2 || card.misses.length > 1) && (
                      <details className="aa-option-card-details">
                        <summary>More detail</summary>
                        {card.solves.length > 2 && (
                          <ul className="aa-option-card-list aa-option-card-list--solves">
                            {card.solves.slice(2).map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        )}
                        {card.misses.length > 1 && (
                          <ul className="aa-option-card-list aa-option-card-list--misses">
                            {card.misses.slice(1).map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        )}
                        <p className="aa-option-card-fine">
                          <strong>IP.</strong> {card.ip_terms} <br />
                          <strong>Compliance.</strong> {card.compliance}
                        </p>
                      </details>
                    )}

                    <button
                      type="button"
                      className="aa-option-card-cta"
                      onClick={() => handlePick(card)}
                      disabled={!!picking || !!paywall}
                    >
                      <span>
                        {isPicking ? 'Routing…' : paywall ? 'Quota reached' : `Pick ${meta.badge}`}
                      </span>
                      {!isPicking && !paywall && <ArrowRight size={14} strokeWidth={2.5} />}
                    </button>
                  </article>
                );
              })}
            </section>

            <section className="aa-options-aside">
              <p>
                Not seeing what you need? <button
                  type="button"
                  className="aa-options-link"
                  onClick={() => navigate('/projects')}
                >
                  Open full scoping →
                </button>
              </p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default OrbitOptions;
